import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';

// Pages
import { LoginPage } from './pages/LoginPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';
import { ProjectListPage } from './pages/ProjectListPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectCreateEditPage } from './pages/ProjectCreateEditPage';
import { ReportPage } from './pages/ReportPage';
import { LawDatabasePage } from './pages/LawDatabasePage';
import { SettingsPage } from './pages/SettingsPage';
import { UserManagementPage } from './pages/admin/UserManagementPage';
import { AllProjectsPage } from './pages/admin/AllProjectsPage';
import { MasterDataPage } from './pages/admin/MasterDataPage';
import { LawAdminPage } from './pages/admin/LawAdminPage';
import { MasterDataAdminPage } from './pages/admin/MasterDataAdminPage';
import { AnalysisDashboardPage } from './pages/AnalysisDashboardPage';
import { LandingPage } from './pages/LandingPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
              <Route path="/invite/:token" element={<InviteAcceptPage />} />

              {/* Protected routes */}
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <ProjectListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/new"
                element={
                  <ProtectedRoute>
                    <ProjectCreateEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <ProtectedRoute>
                    <ProjectDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id/edit"
                element={
                  <ProtectedRoute>
                    <ProjectCreateEditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:id/report"
                element={
                  <ProtectedRoute>
                    <ReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/laws"
                element={
                  <ProtectedRoute>
                    <LawDatabasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis"
                element={
                  <ProtectedRoute>
                    <AnalysisDashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/projects"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AllProjectsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/master"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <MasterDataPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/laws"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <LawAdminPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/master-data"
                element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <MasterDataAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
